/**
 * understandingCard — the live <topic-understanding> chat card ("memory update").
 *
 * A mini polar map on the left, the topic title and its status on the right:
 *
 *     [◔ mini map]  Family & Parenting
 *                   ✓ Memory updated
 *                   [ Reached Intermediate understanding ]
 *
 * The map is a miniature of the /discovery Charted Atlas, so every section
 * close visibly writes into the same picture the user browses on the Progress
 * tab: every topic's slice exists as pale mist, charted understanding sits in
 * each topic's identity hue, and the update plays the atlas beat — survey
 * scan, gather, write with a ripple at the honest landing, edge sharpen and
 * an outward settle wave (miniPolarMap.js owns all of that via `playUpdate`).
 *
 * Four states:
 *   - updating → updated (the ordinary close)
 *   - + milestone pill, when the topic's depth band moved up
 *   - already saved, when a close changed nothing (`changed === false` and the
 *     tier isn't 0). No session close is visually silent.
 *
 * The card is impressionistic by design: the checkmark fires on the instant
 * tier write, and the async narrative pipeline stays invisible here.
 *
 * Tapping anywhere on the card (which carries the shared learn-more chevron)
 * opens the progress view in the right panel. The OrientationMode demo card
 * carries no topic id and is therefore left non-interactive, chevron-free.
 *
 * Animation is JS-driven (rAF, started after the block's fade-in) — there is no
 * JS gating of subsequent content; the card doesn't collapse, so the next
 * bubble simply flows beneath it. `understandingCardHoldMs` is what keeps that
 * bubble from arriving mid-sweep.
 *
 * History (post-reload) does NOT use this — MessageRenderer flattens the tag to
 * a static markdown summary. This module is the live path only.
 *
 * The large BrainViz (/discovery page + <brain-reveal>) is unaffected.
 *
 * @module components/brain/understandingCard
 */

import { DiscoveryAPI } from '../../core/discoveryApi.js';
import BrainViz from './BrainViz.js';
import { loadBrainGeometry } from './brainGeometryLoader.js';
import { levelLabel, levelOf } from '../depthProgress.js';
import { openProgressPanel } from '../progressPanelDrillHelper.js';
import {
  CARD_HOLD_MS,
  DRAMA_DELTA,
  buildMiniMap,
  prefersReducedMotion
} from './miniPolarMap.js';

const discoveryAPI = new DiscoveryAPI();

const NS = 'http://www.w3.org/2000/svg';

/** Rendered size (px) of the mini map. Its viewBox is 112; this is the box. */
const MAP_SIZE = 96;

/** Start the animation even if transitionend never fires. */
const FADE_IN_FALLBACK_MS = 700;

const clamp01 = (n) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));

/**
 * `depthProgress` speaks 0–100; this module speaks 0–1. The rounding also
 * removes the `.toFixed(2)` float epsilon that would otherwise straddle the
 * 1/34/67 band edges.
 * @param {number} v - Depth, 0–1
 * @returns {'none'|'beginning'|'intermediate'|'deep'}
 * @private
 */
const bandOf = (v) => levelOf(Math.round(v * 100));

/**
 * How long a live card keeps animating after its own fade-in — i.e. how much
 * longer any chat content that follows must hold before it appears, so the card
 * doesn't still read "Updating memory" underneath already-visible text. Zero
 * under reduced motion, where `animate()` lands the final state immediately.
 * One conservative constant for all cards: the hold map is looked up per tag,
 * not per payload (`BlockStreamBuffer` calls this getter with no arguments).
 * Consumed by `structuredBlockHoldMs` (structuredBlockRenderers.js).
 * @returns {number}
 */
export function understandingCardHoldMs() {
  return prefersReducedMotion() ? 0 : CARD_HOLD_MS;
}

/**
 * Parse the `<topics>` JSON blob — the 12-topic context for the map.
 *
 * THE UNIT BOUNDARY: the backend sends 0–100 integers, everything downstream of
 * here is 0–1. A malformed blob degrades to `[]` rather than killing the card;
 * the map renders the active wedge plus eleven empty gray slivers.
 *
 * @param {string|null} raw - Raw JSON text (never XML-escaped on this path)
 * @returns {Array<{id: string|null, label: string, progress: number}>} 0–1 values
 * @private
 */
function parseTopics(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((t) => ({
      id: t?.id ?? null,
      label: typeof t?.label === 'string' ? t.label : '',
      progress: clamp01(Number(t?.progress) / 100)
    }));
  } catch (err) {
    console.warn('[understandingCard] malformed <topics> payload:', err);
    return [];
  }
}

/**
 * Parse the nested <topic-understanding> inner XML (v2).
 *
 * Tolerant by design: both ends of this contract are ours, but an in-flight
 * bubble can still carry an older shape, so only `label` and `progress` are
 * required and a missing `<changed>` means TRUE (animate normally) — nothing
 * may rely on the default meaning "no-op". Nothing on this path is
 * XML-escaped: the XML is built client-side and consumed by regex +
 * `JSON.parse` + `textContent`, never by a real parser.
 *
 * @param {string} inner - Inner XML of the block
 * @returns {{topic: string|null, tier: number|null, label: string, from: number,
 *   progress: number, changed: boolean,
 *   topics: Array<{id: string|null, label: string, progress: number}>}|null}
 */
export function parseTopicUnderstanding(inner) {
  const get = (tag) => {
    const m = inner.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
    return m ? m[1].trim() : null;
  };

  const label = get('label');
  const progressStr = get('progress');
  if (!label || progressStr == null) return null;

  const tierStr = get('tier');
  const changedStr = get('changed');

  return {
    topic: get('topic') || null,
    // 0 = L1 flash, 1 = L2 node, 2 = L3 line. Only tier 0 is load-bearing here.
    tier: tierStr == null ? null : parseInt(tierStr, 10),
    label,
    from: clamp01(parseFloat(get('from') || '0')),
    progress: clamp01(parseFloat(progressStr)),
    changed: changedStr == null ? true : changedStr !== 'false',
    topics: parseTopics(get('topics'))
  };
}

/**
 * Whether this payload gets the animated sweep. The L1 flash (tier 0) always
 * does: its payload structurally carries `changed: false` because the tier is
 * persisted before the payload is computed, so it is an acknowledgment of a
 * real write, not a no-op.
 * @param {{changed: boolean, tier: number|null}} data
 * @returns {boolean}
 * @private
 */
const isAnimated = (data) => data.changed !== false || data.tier === 0;

/**
 * The status element — always present, so it reserves its space. Three mutually
 * exclusive states in identical fixed-height boxes, swapped by class on the
 * card, so the flip cannot shift anything vertically. Do NOT switch the
 * container to baseline alignment: the done state's leading <svg> is a replaced
 * element and contributes its bottom margin edge rather than a text baseline,
 * which makes the status visibly jump on the swap.
 * @returns {HTMLElement}
 * @private
 */
function buildStatus() {
  const check =
    '<svg class="tu-card__status-check" viewBox="0 0 16 16" fill="none" stroke="currentColor" ' +
    'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<polyline points="3.5,8.5 6.5,11.5 12.5,5"/></svg>';
  const status = document.createElement('div');
  status.className = 'tu-card__status';
  status.innerHTML =
    '<span class="tu-card__status-working">Updating memory</span>' +
    `<span class="tu-card__status-done">${check}<span>Memory updated</span></span>` +
    `<span class="tu-card__status-saved">${check}<span>Already saved — nothing new this time</span></span>`;
  return status;
}

/**
 * Build the card shell and its map.
 *
 * The active wedge rests at the DRAMATIZED origin, never at the final value, so
 * there is no pre-animation flash of the destination.
 *
 * @param {ReturnType<typeof parseTopicUnderstanding>} data
 * @returns {{card: HTMLElement, map: ReturnType<typeof buildMiniMap>, displayFrom: number}}
 * @private
 */
function buildCard(data) {
  const animated = isAnimated(data);
  // §Dramatized minimum delta: guarantee a substantial sweep, keep the landing exact.
  const displayFrom = Math.max(0, Math.min(data.from, data.progress - DRAMA_DELTA));

  const card = document.createElement('div');
  card.className = 'structured-card tu-card';

  const body = document.createElement('div');
  body.className = 'tu-card__body';

  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'tu-card__map');
  svg.setAttribute('width', String(MAP_SIZE));
  svg.setAttribute('height', String(MAP_SIZE));

  const map = buildMiniMap(svg, {
    topics: data.topics,
    activeTopicId: data.topic,
    activeFrom: animated ? displayFrom : data.progress
  });

  const right = document.createElement('div');
  right.className = 'tu-card__right';

  const label = document.createElement('span');
  label.className = 'tu-card__label';
  label.textContent = data.label;

  // Hidden until earned; revealed by .tu-card--milestone on completion.
  const milestone = document.createElement('div');
  milestone.className = 'tu-card__milestone';
  milestone.textContent = `Reached ${levelLabel(Math.round(data.progress * 100))} understanding`;

  right.append(label, buildStatus(), milestone);
  body.append(svg, right);
  card.append(body);

  // Tap-to-open, whole card, with the shared learn-more chevron at the right
  // edge (same markup as the insight card, so the .structured-card--clickable
  // hover choreography applies unchanged). The demo card (no topic id) is
  // exempt: it has nothing to focus and no panel to open before auth, so it
  // renders without the affordance.
  if (data.topic) {
    const learnMore = document.createElement('span');
    learnMore.className = 'structured-card__learn-more tu-card__learn-more';
    learnMore.innerHTML = `
      <span class="structured-card__learn-more-text">View progress</span>
      <svg class="structured-card__learn-more-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5"
          stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    body.appendChild(learnMore);

    card.classList.add('structured-card--clickable');
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-label', 'Open your understanding map');
    const open = () => openProgressPanel();
    card.addEventListener('click', open);
    card.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      e.preventDefault();
      open();
    });
  }

  return { card, map, displayFrom };
}

/**
 * Drive the card. Everything starts once the block has faded in, so the beat
 * never plays under an invisible card.
 *
 * Animated path: the map's own `playUpdate` beat (scan → gather → write with
 * ripple at the honest landing → sharpen + settle wave); the status flips and
 * the pill appears at the landing (`onLanded`), the map is released when the
 * beat resolves. Already-saved path: no motion at all — the map is built at
 * its full-color rest state and the status goes straight to "already saved".
 *
 * @param {HTMLElement} card
 * @param {ReturnType<typeof parseTopicUnderstanding>} data
 * @param {ReturnType<typeof buildMiniMap>} map
 * @param {number} displayFrom - Dramatized sweep origin, 0–1
 * @private
 */
function animate(card, data, map, displayFrom) {
  const animated = isAnimated(data);

  const complete = () => {
    card.classList.add('tu-card--complete');
    if (!animated) card.classList.add('tu-card--saved');
    // The pill keys off the REAL from/progress, never the dramatized origin:
    // motion may exaggerate, the milestone may not.
    if (data.progress > data.from && bandOf(data.from) !== bandOf(data.progress)) {
      card.classList.add('tu-card--milestone');
    }
  };

  // Reduced motion: no beat — the final full-color state (playUpdate would
  // short-circuit the same way; skipping it avoids even the fade-in wait).
  if (prefersReducedMotion()) {
    card.classList.add('tu-card--animate');
    map.setActive(data.progress);
    complete();
    map.destroy();
    return;
  }

  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    card.classList.add('tu-card--animate');

    if (!animated) {
      // Built at rest (activeFrom === progress); nothing to play.
      complete();
      map.destroy();
      return;
    }

    map
      .playUpdate({ from: displayFrom, to: data.progress, onLanded: complete })
      .then(() => map.destroy());
  };

  // Start after the block's fade-in completes (fadeInBlock transitions opacity),
  // with a fallback in case transitionend doesn't fire.
  const onFadeIn = (e) => {
    if (e.target !== card || e.propertyName !== 'opacity') return;
    card.removeEventListener('transitionend', onFadeIn);
    start();
  };
  card.addEventListener('transitionend', onFadeIn);
  setTimeout(() => {
    card.removeEventListener('transitionend', onFadeIn);
    start();
  }, FADE_IN_FALLBACK_MS);
}

/**
 * Renderer entry point — registered as 'topic-understanding'.
 * Returns a shell element synchronously; the animation runs async.
 * @param {string} innerContent
 * @returns {HTMLElement|null}
 */
export function renderTopicUnderstanding(innerContent) {
  const data = parseTopicUnderstanding(innerContent);
  if (!data) return null;
  const { card, map, displayFrom } = buildCard(data);
  animate(card, data, map, displayFrom); // fire-and-forget
  return card;
}

/**
 * Renderer for <brain-reveal> — a static inline whole-brain at current state
 * (the Foundation-completion milestone moment). Returns a shell synchronously,
 * hydrates async.
 * @returns {HTMLElement}
 */
export function renderBrainReveal() {
  const card = document.createElement('div');
  card.className = 'structured-card tu-reveal';
  const mount = document.createElement('div');
  mount.className = 'tu-reveal__brain';
  card.appendChild(mount);

  Promise.all([loadBrainGeometry(), discoveryAPI.getBrainProgress()])
    .then(([geometry, res]) => {
      const regions = res?.data?.regions || {};
      const sectionCounts = {};
      const state = {};
      for (const [r, info] of Object.entries(regions)) {
        sectionCounts[r] = info.sections;
        state[r] = info.buckets;
      }
      const brain = new BrainViz(mount, { geometry, sectionCounts, reducedMotion: true });
      brain.setProgress(state);
    })
    .catch((err) => { console.warn('[brain-reveal] failed:', err); });

  return card;
}

export default renderTopicUnderstanding;
